/* =================================================================
   br-logo-marquee — responsive recognition-row motion.

   The homepage's grid variant stays a static 5 x 2 wall above 900px.
   At <=900px the same merchant-managed blocks become a CSS transform
   marquee (translateX on the track — not a nested overflow scroller).
   Writing scrollLeft every frame inside -webkit-overflow-scrolling +
   mask-image was the iOS compositor flicker reported on the live
   homepage (handheld capture, 2026-09-04).

   JS only:
     - sets --br-logo-marquee-duration from the measured loop distance
       so the drift stays 21px/s (approved prototype: 0.35px / 60fps)
     - pauses while touched, resumes after the interaction settles
   Hover pause is CSS (@media hover + pointer: fine). Reduced motion
   is CSS (animation: none). Copies two and three are aria-hidden in
   Liquid, so assistive technology reads the recognition list once.
   ================================================================= */
(function () {
  if (window.brLogoMarquee) {
    window.brLogoMarquee.init(document);
    return;
  }

  var RESPONSIVE_QUERY = window.matchMedia('(max-width: 900px)');
  var REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  var SPEED = 21; // px/s — approved prototype: 0.35px per 60fps frame.
  var RESUME_DELAY = 1600;

  function setup(viewport) {
    if (viewport.dataset.brLogoReady === 'true') return;

    var track = viewport.querySelector('.br-logo-marquee__grid-track');
    var sets = track ? track.querySelectorAll('.br-logo-marquee__grid-set') : [];
    if (!track || sets.length < 2) return;

    viewport.dataset.brLogoReady = 'true';

    var resumeTimers = new Map();
    var pauseReasons = new Set();
    var resizeObserver = null;

    function loopDistance() {
      if (!RESPONSIVE_QUERY.matches) return 0;
      return sets[1].offsetLeft - sets[0].offsetLeft;
    }

    function syncDuration() {
      var distance = loopDistance();
      if (!Number.isFinite(distance) || distance <= 0) {
        track.style.removeProperty('--br-logo-marquee-duration');
        return;
      }
      track.style.setProperty('--br-logo-marquee-duration', (distance / SPEED).toFixed(3) + 's');
    }

    function applyPaused() {
      viewport.classList.toggle('is-paused', pauseReasons.size > 0);
    }

    function pause(reason) {
      window.clearTimeout(resumeTimers.get(reason));
      resumeTimers.delete(reason);
      pauseReasons.add(reason);
      applyPaused();
    }

    function resume(reason, delayed) {
      window.clearTimeout(resumeTimers.get(reason));
      resumeTimers.delete(reason);

      var finish = function () {
        resumeTimers.delete(reason);
        pauseReasons.delete(reason);
        applyPaused();
      };

      if (delayed) resumeTimers.set(reason, window.setTimeout(finish, RESUME_DELAY));
      else finish();
    }

    function onModeChange() {
      resumeTimers.forEach(function (timer) {
        window.clearTimeout(timer);
      });
      resumeTimers.clear();
      pauseReasons.clear();
      applyPaused();
      syncDuration();
    }

    viewport.addEventListener(
      'touchstart',
      function () {
        if (RESPONSIVE_QUERY.matches && !REDUCED_MOTION_QUERY.matches) pause('touch');
      },
      { passive: true }
    );
    viewport.addEventListener(
      'touchend',
      function () {
        resume('touch', true);
      },
      { passive: true }
    );
    viewport.addEventListener(
      'touchcancel',
      function () {
        resume('touch', true);
      },
      { passive: true }
    );

    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(function () {
        if (RESPONSIVE_QUERY.matches && !REDUCED_MOTION_QUERY.matches) syncDuration();
      });
      resizeObserver.observe(track);
    }

    if (RESPONSIVE_QUERY.addEventListener) {
      RESPONSIVE_QUERY.addEventListener('change', onModeChange);
      REDUCED_MOTION_QUERY.addEventListener('change', onModeChange);
    } else {
      RESPONSIVE_QUERY.addListener(onModeChange);
      REDUCED_MOTION_QUERY.addListener(onModeChange);
    }

    onModeChange();
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-br-logo-scroller]').forEach(setup);
  }

  window.brLogoMarquee = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
